import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { lazy, Suspense } from "react";
import reifyLogo from "@/assets/reify-logo.png";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";

const MysteryScene = lazy(async () => {
  const module = await import("@/components/three/mystery-scene.tsx");
  return { default: module.MysteryScene };
});

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const fadeScale = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
  },
};

export function HomePage() {
  return (
    <section className="relative h-full overflow-hidden">
      <Suspense fallback={<SceneFallback />}>
        <MysteryScene />
      </Suspense>
      <div className="relative z-10 mx-auto flex h-full w-full max-w-7xl items-center px-5 py-6">
        <motion.div
          className="grid w-full gap-6 lg:grid-cols-[1.18fr_0.82fr] lg:items-center"
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <motion.div variants={fadeUp} className="space-y-6">
            <div className="space-y-4">
              <motion.div variants={fadeUp}>
                <Badge variant="accent">dynamic adventure</Badge>
              </motion.div>
              <div className="space-y-2.5">
                <motion.div className="flex items-end gap-4" variants={fadeUp}>
                  <motion.img
                    src={reifyLogo}
                    alt=""
                    className="hidden h-20 w-20 drop-shadow-[0_0_12px_rgba(88,128,255,0.3)] sm:block lg:h-24 lg:w-24"
                    variants={{
                      hidden: { opacity: 0, scale: 0.8, rotate: -10 },
                      show: {
                        opacity: 1,
                        scale: 1,
                        rotate: 0,
                        transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
                      },
                    }}
                    draggable={false}
                  />
                  <h1 className="font-bold text-5xl tracking-[0.16em] sm:text-7xl lg:text-[7rem] xl:text-[7.5rem]">
                    <span className="reify-rotating-inline-glow reify-glow-pulse" data-text="reify">
                      reify
                    </span>
                  </h1>
                </motion.div>
                <motion.p
                  className="max-w-xl text-foreground/72 text-sm leading-6 sm:text-base"
                  variants={fadeUp}
                ></motion.p>
              </div>
            </div>
            <motion.div className="flex flex-wrap gap-3" variants={fadeUp}>
              <Button asChild size="default" className="shadow-[0_0_24px_rgba(88,128,255,0.18)]">
                <Link to="/download">Download the latest build</Link>
              </Button>
              <Button asChild size="default" variant="outline">
                <Link to="/docs">Read the docs</Link>
              </Button>
            </motion.div>
          </motion.div>
          <motion.div variants={fadeScale}>
            <Card className="border-white/12 bg-black/30 backdrop-blur-2xl">
              <CardHeader>
                <CardTitle>Something is transmitting...</CardTitle>
                <CardDescription>
                  A signal from the deep brings itself into focus. A shout from the void has pulled
                  you out of your slumber.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 border-white/6 border-l-2 pl-4 font-space text-foreground/55 text-sm leading-6">
                  <p>
                    In the year 2531, the extraordinary has become mundane. Yet, there is something
                    new beckoning for you to discover its secrets. You will find it hard to resist
                    the pull of its presence - this is something nobody has ever seen before.
                  </p>
                  <p>
                    Choices carry weight. The narrative remembers what you've done and what you've
                    ignored.
                  </p>
                </div>
                <p className="text-foreground/40 text-xs italic tracking-wide">
                  Powered by local inference. Nothing leaves your machine.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function SceneFallback() {
  return (
    <motion.div
      className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(110,140,255,0.18),transparent_40%),radial-gradient(circle_at_bottom_left,rgba(255,170,140,0.06),transparent_28%),linear-gradient(180deg,rgba(5,6,11,0.98),rgba(5,6,11,0.88))]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    />
  );
}
